/**
 * syncService.js - Serviço de Sincronização com Supabase
 * 
 * Sincroniza dados locais com o banco de dados:
 * - Histórico de respostas (para cards de desempenho)
 * - Gamificação (XP, streak, nível)
 * - Configurações do usuário
 * 
 * NÃO sincroniza:
 * - Repetição espaçada (mantém local - pessoal do usuário)
 */

import { supabase } from '../lib/supabase';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { getCachedUser } from './userCacheService';

// ==================== HISTÓRICO DE RESPOSTAS ====================

/**
 * Registra uma resposta no Supabase
 * Chamado após cada questão respondida
 */
export const syncAnswer = async (questionId, isCorrect, options = {}) => {
    try {
        const user = await getCachedUser();
        if (!user) return { success: false, error: 'Usuário não autenticado' };

        const { data, error } = await supabase
            .from('answer_history')
            .insert({
                user_id: user.id,
                question_id: questionId,
                question_source: options.source || 'user',
                category: options.category || 'GERAL',
                is_correct: isCorrect,
                time_spent_seconds: options.timeSpent || null,
            })
            .select()
            .single();

        if (error) throw error;

        // Atualizar estatísticas agregadas
        await updateQuestionStats(user.id, questionId, isCorrect, options.source);

        // Atualizar gamificação
        const xpGained = isCorrect ? 15 : 10; // XP por resposta
        await updateGamification(user.id, xpGained, isCorrect);

        return { success: true, data };
    } catch (error) {
        // Salvar localmente para sync posterior
        await queueOfflineAnswer(questionId, isCorrect, options);
        return { success: false, error: error.message };
    }
};

/**
 * Atualiza estatísticas agregadas de uma questão
 */
const updateQuestionStats = async (userId, questionId, isCorrect, source = 'user') => {
    try {
        // Tentar atualizar existente
        const { data: existing } = await supabase
            .from('question_stats')
            .select('*')
            .eq('user_id', userId)
            .eq('question_id', questionId)
            .single();

        if (existing) {
            // Atualizar
            await supabase
                .from('question_stats')
                .update({
                    correct_count: existing.correct_count + (isCorrect ? 1 : 0),
                    wrong_count: existing.wrong_count + (isCorrect ? 0 : 1),
                    total_attempts: existing.total_attempts + 1,
                    last_answered: new Date().toISOString(),
                })
                .eq('id', existing.id);
        } else {
            // Criar novo
            await supabase
                .from('question_stats')
                .insert({
                    user_id: userId,
                    question_id: questionId,
                    question_source: source,
                    correct_count: isCorrect ? 1 : 0,
                    wrong_count: isCorrect ? 0 : 1,
                    total_attempts: 1,
                    first_answered: new Date().toISOString(),
                    last_answered: new Date().toISOString(),
                });
        }
    } catch (error) {
        // Erro silencioso - não crítico
    }
};

// ==================== GAMIFICAÇÃO ====================

/**
 * Atualiza gamificação usando a função do banco
 */
const updateGamification = async (userId, xpGained, isCorrect) => {
    try {
        const { data, error } = await supabase
            .rpc('update_gamification', {
                p_user_id: userId,
                p_xp_gained: xpGained,
                p_is_correct: isCorrect,
            });

        if (error) throw error;
        return data;
    } catch (error) {
        return null;
    }
};

/**
 * Obtém dados de gamificação do usuário
 */
export const getGamificationData = async () => {
    try {
        const user = await getCachedUser();
        if (!user) return null;

        const { data, error } = await supabase
            .from('profiles')
            .select(`
                xp_total,
                nivel,
                streak_atual,
                streak_maximo,
                questoes_respondidas,
                questoes_corretas,
                last_study_date,
                daily_goal,
                daily_answered,
                daily_correct
            `)
            .eq('id', user.id)
            .single();

        if (error) throw error;

        // Formatar para o formato esperado pelo app
        return {
            xp: {
                total: data.xp_total || 0,
                level: data.nivel || 1,
            },
            streak: {
                current: data.streak_atual || 0,
                longest: data.streak_maximo || 0,
                lastStudyDate: data.last_study_date,
            },
            dailyChallenge: {
                questionsGoal: data.daily_goal || 10,
                questionsAnswered: data.daily_answered || 0,
                correctAnswers: data.daily_correct || 0,
                completed: (data.daily_answered || 0) >= (data.daily_goal || 10),
            },
            stats: {
                totalAnswered: data.questoes_respondidas || 0,
                totalCorrect: data.questoes_corretas || 0,
                accuracy: data.questoes_respondidas > 0
                    ? Math.round((data.questoes_corretas / data.questoes_respondidas) * 100)
                    : 0,
            },
        };
    } catch (error) {
        return null;
    }
};

/**
 * Adiciona XP manualmente (para conquistas, desafios, etc.)
 *
 * Segurança (#1 / Fase 1b): o incremento é feito no SERVIDOR via RPC
 * `award_xp` (SECURITY DEFINER) — o cliente nunca grava xp_total/nivel direto
 * por este caminho. A versão antiga estava QUEBRADA (atribuía um query-builder à
 * coluna `xp_total`) e era a porta para adulteração de ranking. Ver migration
 * 20260609120000_lock_privileged_profile_columns.sql.
 *
 * Inerte e seguro antes do push: se a RPC ainda não existir no banco, retorna
 * falha silenciosa (NÃO recai no UPDATE direto — não reabrimos a brecha).
 */
export const addXP = async (amount, reason = 'generic') => {
    try {
        const user = await getCachedUser();
        if (!user) return { success: false };

        const { data, error } = await supabase
            .rpc('award_xp', { p_amount: amount, p_reason: reason })
            .single();

        if (error) throw error;

        return { success: true, xp: data.xp_total, level: data.nivel };
    } catch (error) {
        return { success: false, error: error.message };
    }
};

// ==================== ESTATÍSTICAS SEMANAIS ====================

/**
 * Obtém estatísticas da semana para os cards de desempenho
 */
export const getWeeklyStats = async () => {
    try {
        const user = await getCachedUser();
        if (!user) return null;

        // Buscar respostas dos últimos 7 dias
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);

        const { data, error } = await supabase
            .from('answer_history')
            .select('is_correct, category, answered_at')
            .eq('user_id', user.id)
            .gte('answered_at', weekAgo.toISOString())
            .order('answered_at', { ascending: false })
            .limit(500); // Limite otimizado para 7 dias

        if (error) throw error;

        // Processar dados
        const stats = {
            totalAnswered: data.length,
            totalCorrect: data.filter(d => d.is_correct).length,
            accuracy: data.length > 0
                ? Math.round((data.filter(d => d.is_correct).length / data.length) * 100)
                : 0,
            byCategory: {},
            byDay: {},
        };

        // Agrupar por categoria
        data.forEach(item => {
            const cat = item.category || 'GERAL';
            if (!stats.byCategory[cat]) {
                stats.byCategory[cat] = { total: 0, correct: 0 };
            }
            stats.byCategory[cat].total++;
            if (item.is_correct) stats.byCategory[cat].correct++;
        });

        // Agrupar por dia
        data.forEach(item => {
            const day = new Date(item.answered_at).toLocaleDateString('pt-BR', { weekday: 'short' });
            if (!stats.byDay[day]) {
                stats.byDay[day] = { total: 0, correct: 0 };
            }
            stats.byDay[day].total++;
            if (item.is_correct) stats.byDay[day].correct++;
        });

        return stats;
    } catch (error) {
        return null;
    }
};

/**
 * Obtém histórico diário para gráficos
 */
export const getDailyHistory = async (days = 7) => {
    try {
        const user = await getCachedUser();
        if (!user) return [];

        const startDate = new Date();
        startDate.setDate(startDate.getDate() - days);

        const { data, error } = await supabase
            .from('answer_history')
            .select('is_correct, answered_at')
            .eq('user_id', user.id)
            .gte('answered_at', startDate.toISOString())
            .order('answered_at', { ascending: true })
            .limit(500); // Limite otimizado

        if (error) throw error;

        // Agrupar por data
        const dailyData = {};
        for (let i = 0; i <= days; i++) {
            const date = new Date();
            date.setDate(date.getDate() - i);
            const dateStr = date.toISOString().split('T')[0];
            dailyData[dateStr] = { total: 0, correct: 0, date: dateStr };
        }

        data.forEach(item => {
            const dateStr = new Date(item.answered_at).toISOString().split('T')[0];
            if (dailyData[dateStr]) {
                dailyData[dateStr].total++;
                if (item.is_correct) dailyData[dateStr].correct++;
            }
        });

        return Object.values(dailyData).reverse();
    } catch (error) {
        return [];
    }
};

// ==================== CONFIGURAÇÕES DO USUÁRIO ====================

/**
 * Obtém configurações do usuário
 */
export const getUserSettings = async () => {
    try {
        const user = await getCachedUser();
        if (!user) return null;

        const { data, error } = await supabase
            .from('user_settings')
            .select('*')
            .eq('user_id', user.id)
            .single();

        if (error && error.code !== 'PGRST116') throw error; // PGRST116 = not found

        // Se não existir, criar com valores padrão
        if (!data) {
            const { data: newSettings } = await supabase
                .from('user_settings')
                .insert({ user_id: user.id })
                .select()
                .single();
            return newSettings;
        }

        return data;
    } catch (error) {
        return null;
    }
};

/**
 * Atualiza configurações do usuário
 */
export const updateUserSettings = async (updates) => {
    try {
        const user = await getCachedUser();
        if (!user) return { success: false };

        const { data, error } = await supabase
            .from('user_settings')
            .upsert({
                user_id: user.id,
                ...updates,
                updated_at: new Date().toISOString(),
            })
            .select()
            .single();

        if (error) throw error;
        return { success: true, data };
    } catch (error) {
        return { success: false, error: error.message };
    }
};

/**
 * Atualiza cronograma de revisões
 */
export const updateReviewSchedule = async (schedule) => {
    return updateUserSettings({ review_schedule: schedule });
};

/**
 * Atualiza meta diária
 */
export const updateDailyGoal = async (goal) => {
    try {
        const user = await getCachedUser();
        if (!user) return { success: false };

        // Atualizar em profiles também
        await supabase
            .from('profiles')
            .update({ daily_goal: goal })
            .eq('id', user.id);

        return updateUserSettings({ daily_goal: goal });
    } catch (error) {
        return { success: false };
    }
};

// ==================== SYNC OFFLINE ====================

const OFFLINE_QUEUE_KEY = '@sync_offline_queue';

/**
 * Adiciona resposta à fila offline
 */
const queueOfflineAnswer = async (questionId, isCorrect, options) => {
    try {
        const queue = await AsyncStorage.getItem(OFFLINE_QUEUE_KEY);
        const items = queue ? JSON.parse(queue) : [];

        items.push({
            type: 'answer',
            questionId,
            isCorrect,
            options,
            timestamp: Date.now(),
        });

        await AsyncStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(items));
    } catch (error) {
        // Erro silencioso - não crítico
    }
};

/**
 * Processa fila offline quando voltar a ter conexão
 */
export const processOfflineQueue = async () => {
    try {
        const queue = await AsyncStorage.getItem(OFFLINE_QUEUE_KEY);
        if (!queue) return;

        const items = JSON.parse(queue);
        if (items.length === 0) return;

        console.log(`Processando ${items.length} itens offline...`);

        for (const item of items) {
            if (item.type === 'answer') {
                await syncAnswer(item.questionId, item.isCorrect, item.options);
            }
        }

        // Limpar fila após processar
        await AsyncStorage.removeItem(OFFLINE_QUEUE_KEY);
    } catch (error) {
        // Erro silencioso
    }
};

// ==================== EXPORTS ====================

export default {
    // Respostas
    syncAnswer,

    // Gamificação
    getGamificationData,
    addXP,

    // Estatísticas
    getWeeklyStats,
    getDailyHistory,

    // Configurações
    getUserSettings,
    updateUserSettings,
    updateReviewSchedule,
    updateDailyGoal,

    // Offline
    processOfflineQueue,
};
